import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import Marquee from "@/components/Marquee";
import Ornament from "@/components/Ornament";
import FancyTitle from "@/components/FancyTitle";
import KitPhotos from "@/components/KitPhotos";
import { api, AIRBNB_URL } from "@/lib/config";
import { formatUKDate } from "@/lib/date";
import { TESTIMONIALS as SHARED_TESTIMONIALS } from "@/data/testimonials";
import { Music, MapPin, ArrowRight } from "lucide-react";

const IMG = {
  hero: "/img/music-room.jpg",
  piano: "/img/music-room.jpg",
  desk: "/img/desk.jpg",
  magic: "/img/star-arch.jpg",
  church: "/img/church.jpg",
  broads: "https://images.pexels.com/photos/32680286/pexels-photo-32680286.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
  garden: "/img/river-norfolk.jpg",
  attic: "/img/attic.jpg",
  holkham: "https://images.unsplash.com/photo-1604086594883-027611f51cf6?auto=format&fit=crop&w=1200&q=80",
};

const TESTIMONIALS = SHARED_TESTIMONIALS;

export default function Home() {
  const [upcoming, setUpcoming] = useState([]);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/blog").then((r) => {
      const all = r.data || [];
      setUpcoming(all.filter((p) => p.upcoming));
      setRecent(all.filter((p) => !p.upcoming).slice(0, 3));
    }).catch(() => {});
  }, []);

  return (
    <>
      <Navigation />

      {/* HERO — paper frontispiece for the letter */}
      <section className="relative min-h-[66vh] w-full flex flex-col items-center justify-center text-center px-6 pt-28 pb-16 border-b border-ink/15" data-testid="hero-section">
        <div className="eyebrow text-ink/65 mb-6 will-fade">Est. 1770 · Stoke Ferry</div>
        <FancyTitle as="h1" className="press-title text-4xl sm:text-5xl lg:text-6xl font-light text-ink leading-[1.15] max-w-5xl uppercase tracking-[0.28em] sm:tracking-[0.34em] will-fade">
          The Magicians' House
        </FancyTitle>
        <div className="font-sans text-[0.66rem] uppercase tracking-[0.4em] text-ink/55 mt-7 will-fade">
          Sleeps Twelve · One Grand Piano
        </div>
        <div className="mt-10 flex flex-col sm:flex-row gap-4 will-fade">
          <Link
            to="/enquire"
            className="bg-burgundy text-parchment px-6 py-3 font-sans text-[0.7rem] uppercase tracking-[0.26em] hover:bg-ink transition-colors inline-flex items-center justify-center gap-2 min-w-[180px]"
            data-testid="hero-enquire-btn"
          >
            Enquire
          </Link>
          <a
            href={AIRBNB_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="border-2 border-ink text-ink px-6 py-3 font-sans text-[0.7rem] uppercase tracking-[0.26em] hover:bg-ink hover:text-parchment transition-colors inline-flex items-center justify-center min-w-[180px]"
            data-testid="hero-airbnb-btn"
          >
            View on Airbnb
          </a>
        </div>
      </section>

      {/* OPENING LETTER */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="opening-letter">
        <div className="eyebrow text-center">A Letter from the House</div>
        <Ornament />
        <p className="font-serif text-lg md:text-xl text-ink/85 leading-relaxed">
          The Magicians' House stands opposite the old parish church in Stoke Ferry,
          a village of flint walls and chimneys tucked into West Norfolk. The first
          house was built in the 18th century for merchants who traded wool and grain
          along the Wissey, at what was then a significant water trading post. The
          Micklefield family grew the site roof by roof, and garden by garden, over
          three generations. In the last century, the grounds were host to the annual
          Stoke Ferry Show, and the gardens were open to the public, then reaching as
          far back as Boughton Fen.
        </p>
        <p className="font-serif text-lg md:text-xl text-ink/85 leading-relaxed mt-6">
          The Magicians' House sleeps twelve, and has acres of private walled gardens
          for you to enjoy. We are dog friendly.
        </p>
      </section>

      {/* AUDIENCES — three ledger blocks */}
      <section className="bg-parchment-cream-noise border-y border-ink/20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
          {/* CREATIVES */}
          <div id="creatives" className="grid md:grid-cols-2 gap-10 md:gap-16 items-center pt-8 pb-16 border-t border-ink/20" data-testid="creatives-section">
            <div>
              <img src="/seal.png" alt="" aria-hidden className="w-12 h-12 mb-4 -ml-1" draggable={false} />
              <h3 className="font-display text-3xl md:text-4xl text-ink mt-2 leading-tight">
                For artists in residence
              </h3>
              <p className="font-serif text-ink/80 mt-5 leading-relaxed">
                Writers, composers, devisers of shows — the house is given over to you.
                A Scheidmayer grand in a music room with fabulous acoustics, a library for
                quiet mornings with a long table for read-throughs. If you want to do a test
                performance, we help you promote it. Residencies
                of a weekend, a week, or a fortnight.
              </p>
              <ul className="mt-6 space-y-2 font-serif text-ink/75">
                <li className="flex gap-3"><span className="text-ink/40">—</span> Grand piano</li>
                <li className="flex gap-3"><span className="text-ink/40">—</span> Writing desks in every bedroom</li>
                <li className="flex gap-3"><span className="text-ink/40">—</span> Tailored pricing by application</li>
              </ul>
            </div>
            <img src={IMG.piano} alt="Grand piano in the drawing room" className="w-full aspect-[4/5] object-cover object-[18%_center] grayscale-[0.1]" />
          </div>

          {/* PILGRIMS */}
          <div id="pilgrims" className="grid md:grid-cols-2 gap-10 md:gap-16 items-center py-16 border-t border-ink/20" data-testid="pilgrims-section">
            <div>
              <img src="/seal.png" alt="" aria-hidden className="w-12 h-12 mb-4 -ml-1" draggable={false} />
              <h3 className="font-display text-3xl md:text-4xl text-ink mt-2 leading-tight">
                For pilgrims, walkers & folklorists
              </h3>
              <p className="font-serif text-ink/80 mt-5 leading-relaxed">
                We keep a small library of route maps, saints' tales and Norfolk folklore
                for guests who would like to walk with purpose.
              </p>
              <ul className="mt-6 space-y-2 font-serif text-ink/75">
                <li className="flex gap-3"><span className="text-ink/40">—</span> River walks</li>
                <li className="flex gap-3"><span className="text-ink/40">—</span> Folklore library for slow evenings</li>
                <li className="flex gap-3">
                  <span className="text-ink/40">—</span>
                  <span>
                    Walk The Merchants and Pilgrims Way, our official partnership with{" "}
                    <a
                      href="https://www.britishpilgrimage.org/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-burgundy hover:text-ink transition-colors"
                      data-testid="pilgrims-bpt-link"
                    >
                      The British Pilgrimage Trust
                    </a>
                  </span>
                </li>
              </ul>
              <a
                href="https://www.britishpilgrimage.org/portfolio/merchants-and-pilgrims-way-kings-lynn-to-ely"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 mt-8 bg-burgundy text-parchment px-7 py-4 font-sans text-[0.74rem] uppercase tracking-[0.26em] hover:bg-ink transition-colors"
                data-testid="pilgrims-route-cta"
              >
                The Merchants and Pilgrims Way <ArrowRight className="w-4 h-4 shrink-0" strokeWidth={1.5} />
              </a>
            </div>
            <img src={IMG.church} alt="A writing desk overlooking All Saints' Church, Stoke Ferry" className="w-full h-auto object-contain" />
          </div>

          {/* FAMILIES */}
          <div id="families" className="grid md:grid-cols-2 gap-10 md:gap-16 items-center py-16 border-t border-ink/20" data-testid="families-section">
            <img src="/img/garden-roses.jpg" alt="Roses in the walled garden" className="w-full aspect-[4/5] object-cover order-2 md:order-1" />
            <div className="order-1 md:order-2">
              <img src="/seal.png" alt="" aria-hidden className="w-12 h-12 mb-4 -ml-1" draggable={false} />
              <h3 className="font-display text-3xl md:text-4xl text-ink mt-2 leading-tight">
                For magical family gatherings
              </h3>
              <p className="font-serif text-ink/80 mt-5 leading-relaxed">
                Children's birthdays are our quiet speciality. We prepare parchment invitations
                sealed in wax, a scavenger hunt through the mature gardens — different
                clues inside, if the weather is bad.
              </p>
              <p className="font-serif text-ink/80 mt-4 leading-relaxed">
                The house welcomes the whole clan. Forage Norfolk for wild garlic, samphire or
                autumn mushrooms, then cook and eat together in the amply-sized kitchen.
              </p>
              <ul className="mt-6 space-y-2 font-serif text-ink/75">
                <li className="flex gap-3"><span className="text-ink/40">—</span> Invitations can be posted ahead of your stay upon timely request</li>
              </ul>
            </div>
          </div>

          {/* KIT HESKETH-HARVEY */}
          <div id="kit" className="grid md:grid-cols-2 gap-10 md:gap-16 items-center py-16 border-t border-ink/20" data-testid="kit-section">
            <img
              src="/img/kit.webp"
              alt="Kit Hesketh-Harvey"
              className="w-full aspect-[4/5] object-cover order-2 md:order-1"
            />
            <div className="order-1 md:order-2">
              <img src="/seal.png" alt="" aria-hidden className="w-12 h-12 mb-4 -ml-1" draggable={false} />
              <h3 className="font-display text-3xl md:text-4xl text-ink mt-2 leading-tight">
                Kit Hesketh-Harvey
              </h3>
              <p className="font-serif text-ink/80 mt-5 leading-relaxed">
                The Archive Room at The Magicians' House is home to the professional archive
                of the late lyricist, librettist, cabaret artist and unparalleled pantomime
                villain, Kit Hesketh-Harvey.
              </p>
              <p className="font-serif text-ink/80 mt-4 leading-relaxed">
                Fans of his work, or those who have business to conduct with it, can visit our
                library of his script proofs and his personal collection of plays, poetry and
                books about the arts. Artists can work amongst his handwritten notebooks,
                developing their own writing in proximity to his. And those seeking inspiration
                can sit in his own wing-backed armchair, where so many great ideas have formed,
                and ruminate upon the room’s view of All Saints Church.
              </p>
              <p className="font-serif text-ink/80 mt-4 leading-relaxed">
                The Archive Room has a large table with comfortable chairs for up to nine.
              </p>
              <p className="font-serif text-ink/80 mt-4 leading-relaxed">
                Or just soak up the atmosphere…
              </p>
              <KitPhotos />
              <Link
                to="/enquire?purpose=archive"
                className="inline-flex items-center gap-3 mt-8 bg-burgundy text-parchment px-7 py-4 font-sans text-[0.74rem] uppercase tracking-[0.26em] hover:bg-ink transition-colors"
                data-testid="kit-archive-visit-cta"
              >
                Arrange an Archive visit <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* NORFOLK ADVENTURES */}
      <Marquee starsOnly />
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="adventures-section">
        <div className="grid md:grid-cols-2 gap-16 items-start">
          <div className="md:sticky md:top-28">
            <h2 className="font-display text-2xl md:text-3xl text-ink leading-tight italic">
              Two hours from London, another century altogether.
            </h2>
            <p className="font-serif text-ink/80 mt-6 leading-relaxed">
              Stoke Ferry is easily reached from King's Cross via Downham Market — roughly
              ninety minutes on a good train, a taxi of ten minutes the other side. Also
              a fine launching pad for deeper Norfolk: the Broads to the east, the
              north coast at Holkham and Wells, the Brecks and the fens to the south.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <img src={IMG.broads} alt="The Broads" className="w-full aspect-[16/10] object-cover" />
              <div className="flex justify-between mt-3 font-sans text-[0.72rem] uppercase tracking-[0.22em] text-ink/60">
                <span>The Norfolk Broads</span>
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" strokeWidth={1.5} /> 1 hr</span>
              </div>
            </div>
            <div>
              <img src={IMG.garden} alt="Reed-lined river on the West Norfolk fens" className="w-full aspect-[3/4] object-cover" />
              <div className="flex justify-between mt-3 font-sans text-[0.68rem] uppercase tracking-[0.22em] text-ink/60">
                <span>River & gardens</span>
                <span>At the gate</span>
              </div>
            </div>
            <div>
              <img src={IMG.holkham} alt="Holkham beach — pine woods and wide sands" className="w-full aspect-[3/4] object-cover" />
              <div className="flex justify-between mt-3 font-sans text-[0.68rem] uppercase tracking-[0.22em] text-ink/60">
                <span>North coast</span>
                <span>45 min</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="bg-parchment-cream-noise border-y border-ink/20" data-testid="pricing-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-24 md:py-28 text-center">
          <div className="eyebrow">Rates</div>
          <Ornament />
          <div className="font-serif text-lg text-ink/85 mt-8 leading-relaxed max-w-2xl mx-auto space-y-2" data-testid="rates-list">
            <p>The Music Room … £25 per hour (bulk discounts for music teachers — please enquire)</p>
            <p>The Walled Gardens … £50 per session (morning 9am–1pm, afternoon 1–5pm, evening 5–10pm)</p>
            <p>The Magicians' House &amp; Private Garden … £850 per night (4pm–10am)</p>
          </div>
          <p className="font-serif text-lg text-ink/85 mt-8 leading-relaxed max-w-2xl mx-auto">
            Please note: we are able to tailor pricing to group size and/or for artists in
            residence if you book through the website. You also will not pay platform fees.
          </p>
          <p className="font-serif text-lg text-ink/85 mt-6 leading-relaxed max-w-2xl mx-auto">
            Please book by enquiry if your group is smaller than 12.<br />
            Let us know if your intention is to work.
          </p>
          <div className="mt-10">
            <Link
              to="/enquire"
              className="inline-flex items-center justify-center bg-burgundy text-parchment px-6 py-3 font-sans text-[0.7rem] uppercase tracking-[0.26em] hover:bg-ink transition-colors"
              data-testid="pricing-enquire-cta"
            >
              Make Enquiry
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-ink text-parchment py-24 md:py-28" data-testid="testimonials-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="eyebrow" style={{ color: "#E8DBD5", opacity: 0.7 }}>What guests have said</div>
          <h2 className="press-title text-4xl md:text-5xl text-parchment mt-3">Marginalia</h2>
          <div className="grid md:grid-cols-2 gap-10 md:gap-16 mt-14">
            {TESTIMONIALS.map((t, i) => (
              <figure key={i} className="border-t border-parchment/30 pt-8" data-testid={`testimonial-${i}`}>
                <Music className="w-5 h-5 text-parchment/60 mb-5" strokeWidth={1.3} />
                <blockquote className="font-display text-lg md:text-xl leading-relaxed text-parchment">
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <figcaption className="mt-6 font-sans text-[0.72rem] uppercase tracking-[0.22em] text-parchment/60">
                  {t.by}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* JOURNAL */}
      {(upcoming.length > 0 || recent.length > 0) && (
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="journal-teaser">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div>
            <h2 className="font-display text-4xl md:text-5xl text-ink mt-3">Things that have happened here.</h2>
          </div>
          <Link to="/diary" className="font-sans text-[0.72rem] uppercase tracking-[0.22em] text-ink/70 hover:text-ink inline-flex items-center gap-2" data-testid="view-all-journal">
            Read the full diary <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>

        {upcoming.length > 0 && (
          <div className="mb-10 border border-ink/20 p-6 md:p-8 bg-parchment-cream-noise" data-testid="upcoming-events">
            <div className="eyebrow">Upcoming</div>
            {upcoming.map((p) => (
              <Link key={p.id} to={`/diary/${p.slug}`} className="block mt-2 group">
                <h3 className="font-display text-2xl md:text-3xl text-ink group-hover:text-ink/70 transition-colors">{p.title}</h3>
                <p className="font-serif text-ink/75 mt-2">{p.excerpt}</p>
                <div className="mt-3 font-sans text-[0.68rem] uppercase tracking-[0.22em] text-ink/60">{formatUKDate(p.published_at)}</div>
              </Link>
            ))}
          </div>
        )}

        {recent.length > 0 && (
        <div className="grid md:grid-cols-3 gap-10 border-t border-ink/20 pt-10">
          {recent.map((p) => (
            <Link key={p.id} to={`/diary/${p.slug}`} className="group" data-testid={`journal-card-${p.slug}`}>
              <div className="overflow-hidden">
                {p.cover_image && <img src={p.cover_image} alt={p.title} className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-[1.03]" />}
              </div>
              <div className="mt-4">
                <div className="font-sans text-[0.68rem] uppercase tracking-[0.22em] text-ink/60">{formatUKDate(p.published_at)}</div>
                <h3 className="font-display text-2xl text-ink mt-2 leading-tight group-hover:text-ink/70 transition-colors">{p.title}</h3>
                <p className="font-serif text-ink/75 mt-2 text-sm leading-relaxed">{p.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
        )}
      </section>
      )}

      {/* CTA STRIP */}
      <section className="bg-parchment-cream-noise border-y border-ink/20" data-testid="cta-strip">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-20 text-center">
          <div className="eyebrow">Write to the House</div>
          <p className="font-serif text-ink/80 mt-5 max-w-xl mx-auto leading-relaxed">
            Tell us who is coming, what you'd like to make or walk or celebrate.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/enquire" className="bg-burgundy text-parchment px-8 py-4 font-sans text-[0.78rem] uppercase tracking-[0.28em] hover:bg-ink transition-colors" data-testid="cta-enquire">
              Begin an enquiry
            </Link>
            <a href={AIRBNB_URL} target="_blank" rel="noopener noreferrer" className="border-2 border-ink px-8 py-4 font-sans text-[0.78rem] uppercase tracking-[0.28em] hover:bg-ink hover:text-parchment transition-colors" data-testid="cta-airbnb">
              Or book on Airbnb
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
